import React, { useState, useEffect, useContext, useMemo } from "react";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Switch } from "@mui/material";
import { MailerSendTemplateService } from "../../services/mailersend-template.service";
import { MailerSendTemplate } from "../../models/mailersend-template.model";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { ToastVariants } from "../../models";
import { Button, TextInput } from "../../components";

type ActiveFilter = "all" | "active" | "inactive";

const emptyTemplate: MailerSendTemplate = {
  templateKey: "",
  mailersendTemplateId: "",
  description: "",
  requiredVariables: "",
  fromEmailOverride: "",
  fromNameOverride: "",
  active: false,
};

const MailerSendTemplatesList = () => {
  const toast = useContext(ToastContext);
  const [templates, setTemplates] = useState<MailerSendTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<MailerSendTemplate>(emptyTemplate);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingTemplate, setDeletingTemplate] = useState<MailerSendTemplate | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [searchKey, setSearchKey] = useState("");
  const [searchId, setSearchId] = useState("");

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (activeFilter === "active" && !t.active) return false;
      if (activeFilter === "inactive" && t.active) return false;
      if (searchKey && !t.templateKey.toLowerCase().includes(searchKey.toLowerCase())) return false;
      if (searchId && !t.mailersendTemplateId.toLowerCase().includes(searchId.toLowerCase())) return false;
      return true;
    });
  }, [templates, activeFilter, searchKey, searchId]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await MailerSendTemplateService.getAll();
      setTemplates(data);
    } catch {
      toast?.openToast("Failed to load templates", 2000, ToastVariants.Error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(emptyTemplate);
    setIsEditing(false);
    setDialogOpen(true);
  };

  const handleEdit = (template: MailerSendTemplate) => {
    setEditingTemplate({ ...template });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingTemplate.templateKey.trim()) {
      toast?.openToast("Template key is required", 2000, ToastVariants.Error);
      return;
    }
    if (!editingTemplate.mailersendTemplateId.trim()) {
      toast?.openToast("MailerSend template ID is required", 2000, ToastVariants.Error);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        templateKey: editingTemplate.templateKey,
        mailersendTemplateId: editingTemplate.mailersendTemplateId,
        description: editingTemplate.description || undefined,
        requiredVariables: editingTemplate.requiredVariables || undefined,
        fromEmailOverride: editingTemplate.fromEmailOverride || undefined,
        fromNameOverride: editingTemplate.fromNameOverride || undefined,
        active: editingTemplate.active,
      };

      if (isEditing && editingTemplate.templateId) {
        const updated = await MailerSendTemplateService.update(editingTemplate.templateId, payload);
        if (updated) {
          toast?.openToast("Template updated");
          setDialogOpen(false);
          fetchTemplates();
        } else {
          toast?.openToast("Failed to update template", 2000, ToastVariants.Error);
        }
      } else {
        const created = await MailerSendTemplateService.create(payload);
        if (created) {
          toast?.openToast("Template created");
          setDialogOpen(false);
          fetchTemplates();
        } else {
          toast?.openToast("Failed to create template", 2000, ToastVariants.Error);
        }
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template: MailerSendTemplate) => {
    try {
      const updated = await MailerSendTemplateService.update(template.templateId!, {
        templateKey: template.templateKey,
        mailersendTemplateId: template.mailersendTemplateId,
        description: template.description,
        requiredVariables: template.requiredVariables,
        fromEmailOverride: template.fromEmailOverride,
        fromNameOverride: template.fromNameOverride,
        active: !template.active,
      });
      if (updated) {
        toast?.openToast(`Template "${template.templateKey}" ${!template.active ? "activated" : "deactivated"}`);
        fetchTemplates();
      } else {
        toast?.openToast("Failed to toggle template", 2000, ToastVariants.Error);
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    }
  };

  const handleDeleteConfirm = (template: MailerSendTemplate) => {
    setDeletingTemplate(template);
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingTemplate?.templateId) return;
    try {
      const success = await MailerSendTemplateService.remove(deletingTemplate.templateId);
      if (success) {
        toast?.openToast("Template deleted");
        fetchTemplates();
      } else {
        toast?.openToast("Failed to delete template", 2000, ToastVariants.Error);
      }
    } catch {
      toast?.openToast("An error occurred", 2000, ToastVariants.Error);
    } finally {
      setDeleteConfirmOpen(false);
      setDeletingTemplate(null);
    }
  };

  return (
    <main>
      <div className="container mx-auto p-8">
        <div className="block lg:flex justify-between items-center mb-6">
          <div className="mb-5 lg:mb-0">
            <h2 className="heading mb-0 uppercase whitespace-nowrap">MailerSend Templates</h2>
          </div>
          <div className="block md:flex lg:justify-end flex-wrap w-full">
            <Button text="Create Template" onClick={handleCreate} className="whitespace-nowrap w-auto mt-0" />
          </div>
        </div>

        <div className="flex flex-wrap gap-4 items-end mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={activeFilter}
              onChange={e => setActiveFilter(e.target.value as ActiveFilter)}
              className="border border-gray-300 rounded px-3 py-2 text-sm min-w-[140px]"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Template Key</label>
            <input
              type="text"
              value={searchKey}
              onChange={e => setSearchKey(e.target.value)}
              placeholder="Search by key..."
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">MailerSend ID</label>
            <input
              type="text"
              value={searchId}
              onChange={e => setSearchId(e.target.value)}
              placeholder="Search by ID..."
              className="border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <CircularProgress />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {templates.length === 0 ? "No MailerSend templates configured" : "No templates match the current filters"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Template Key</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">MailerSend ID</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">MailerSend</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Active</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Description</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">From Override</th>
                  <th className="py-3 px-4 text-sm font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map(template => (
                  <tr key={template.templateId} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm font-medium">{template.templateKey}</td>
                    <td className="py-3 px-4 text-sm text-gray-600 font-mono">{template.mailersendTemplateId}</td>
                    <td className="py-3 px-4 text-sm">
                      {template.mailersendTemplateId ? (
                        <a
                          href={`https://app.mailersend.com/templates/${template.mailersendTemplateId}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-docsigna-purple-dark hover:underline"
                        >
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <Switch checked={template.active} onChange={() => handleToggleActive(template)} size="small" />
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">{template.description || "—"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {template.fromEmailOverride ? `${template.fromNameOverride || "—"} <${template.fromEmailOverride}>` : "Default"}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        <button onClick={() => handleEdit(template)} className="text-sm text-docsigna-purple-dark hover:underline">
                          Edit
                        </button>
                        <button onClick={() => handleDeleteConfirm(template)} className="text-sm text-red-600 hover:underline">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Template" : "Create Template"}</DialogTitle>
        <DialogContent>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Key *</label>
              <TextInput
                value={editingTemplate.templateKey}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingTemplate({ ...editingTemplate, templateKey: e.target.value })}
                placeholder="e.g. verify-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">MailerSend Template ID *</label>
              <TextInput
                value={editingTemplate.mailersendTemplateId}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditingTemplate({ ...editingTemplate, mailersendTemplateId: e.target.value })
                }
                placeholder="e.g. jpzkmgq7z3xl059v"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <TextInput
                value={editingTemplate.description || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                placeholder="What is this template for?"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Required Variables</label>
              <TextInput
                value={editingTemplate.requiredVariables || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditingTemplate({ ...editingTemplate, requiredVariables: e.target.value })
                }
                placeholder='e.g. ["name", "verificationLink"]'
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Email Override</label>
              <TextInput
                value={editingTemplate.fromEmailOverride || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditingTemplate({ ...editingTemplate, fromEmailOverride: e.target.value })
                }
                placeholder="Leave empty for default (info@docnote.com)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From Name Override</label>
              <TextInput
                value={editingTemplate.fromNameOverride || ""}
                onChangeFunc={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEditingTemplate({ ...editingTemplate, fromNameOverride: e.target.value })
                }
                placeholder="Leave empty for default (Docnote)"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={editingTemplate.active}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditingTemplate({ ...editingTemplate, active: e.target.checked })}
              />
              <label className="text-sm font-medium text-gray-700">Active</label>
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button text="Cancel" onClick={() => setDialogOpen(false)} variant="Outlined" width="fit" />
          <Button text={saving ? "Saving..." : "Save"} onClick={handleSave} disabled={saving} width="fit" />
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <p className="text-sm text-gray-600">
            Are you sure you want to delete <strong>{deletingTemplate?.templateKey}</strong>? This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button text="Cancel" onClick={() => setDeleteConfirmOpen(false)} variant="Outlined" width="fit" />
          <Button text="Delete" onClick={handleDelete} variant="orange" width="fit" />
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default MailerSendTemplatesList;
